
import { logError } from "./app";

export const APP_NAME = 'Coherence';

const PREFIX = 'coherence-';
export enum StoreValue { // do not rename options here or stored values will be lost
  start_count = PREFIX + 'start-count',
  session_stats = PREFIX + 'session-stats',
  settings = PREFIX + 'settings',
  notifications = PREFIX + 'notifications',
  userinfo = PREFIX + 'userinfo',
  favorite_time = PREFIX + 'favorite_time',
  /** analytics enabled flag, disabled when value is present */
  analytics = PREFIX + 'analytics',
  /** donate state */
  donate = PREFIX + 'donate',
  /** donate state */
  last_version = PREFIX + 'last-version',
}

/**
 * Represents the user
 */
export interface User {
  /** 
  * User name, can be empty string
  */
  name: string;
}

export type Theme = 'dark' | 'light' | 'auto';

/**
 * Stored settings
 */
export interface Settings {
  version: number;
  sound_effects_enabled: boolean;
  end_sound_enabled: boolean,
  vibrations_enabled: boolean;
  background_music_enabled: boolean;
  messages_enabled: boolean;
  breathing_time: number;
  breathing_time_out: number;
  default_session_time: number;
  theme: Theme;
}

export enum SETTINGS {
  sound_effects_enabled = 'sound_effects_enabled',
  end_sound_enabled = 'end_sound_enabled',
  vibrations_enabled = 'vibrations_enabled',
  background_music_enabled = 'background_music_enabled',
  messages_enabled = 'messages_enabled',
  breathing_time = 'breathing_time',
  breathing_time_out = 'breathing_time_out',
  default_session_time = 'default_session_time',
  theme = 'theme',
}


export const DEFAULT_SETTINGS: Settings = Object.freeze({ // default settings
  /** 
   * Version history:
   * 4: end_sound_enabled added
   * 3: breathing_time added for exhale time
   * 2: theme added
   */
  version: 4,
  sound_effects_enabled: false,
  end_sound_enabled: true,
  vibrations_enabled: true,
  background_music_enabled: true,
  messages_enabled: true,
  breathing_time: 5,
  breathing_time_out: 5,
  default_session_time: 5,
  theme: 'light',
});

export const isSettingEnabled = (setting: SETTINGS): boolean => {
  const value = getSetting(setting);
  if (value) { // non-empty string, true, 42, Infinity, [], ...
    return true;
  }
  return false;
}

export const setSettings = (settings: Settings): void => {
  if (!settings) throw Error('missing setting parameter');
  const prevSettings = getSettings(false);
  // merge the previous settings with the new, update the version before save
  const newSettings = { ...prevSettings, ...settings, ...{ version: DEFAULT_SETTINGS.version } };
  setStoredValue(StoreValue.settings, newSettings);
  console.debug('settings: saved', newSettings);
}

/**
 * @deprecated use AppState or setSettings() 
 */
export const setSetting = (setting: keyof Settings, value: object | number | string | boolean | Array<any>): void => { // eslint-disable-line  @typescript-eslint/no-explicit-any
  if (!setting) throw Error('missing setting parameter');
  const settings = getSettings();
  // update the settings
  settings[setting] = value;
  // save
  setStoredValue(StoreValue.settings, settings);
  // updated setting
  console.debug(`settings:setSetting(${setting}): ${value}`);
}

/**
 * 
 * @returns A raw
 */
export const getSettings = (upgrade: boolean = true): Settings => {
  const settings = getStoredValue(StoreValue.settings, { ...DEFAULT_SETTINGS }) as Settings;
  if (upgrade && settings.version !== DEFAULT_SETTINGS.version) {
    console.debug(`settings: version change detected v${settings.version}->v${DEFAULT_SETTINGS.version}, upgrading`);
    // v2 -> v3 breathing_time_out defaults to existing breathing_time
    if (settings.breathing_time_out === undefined) {
      console.debug(`settings: updating breathing_time_out=${settings.breathing_time}`);
      settings.breathing_time_out = settings.breathing_time;
    }
    // update settings, will rewrite the version to the current one
    setSettings(settings);
  }
  // returns a copy of default settings if not found
  // merge DEFAULT_SETTINGS with the stored settings
  // so new properties that are missing in the store are defaulted
  return { ...DEFAULT_SETTINGS, ...settings };
}

export function getSetting<K extends keyof Settings>(setting: K): Settings[K] {
  if (!setting) throw Error('settings: missing setting parameter');
  const settings = getSettings();
  // Returns DEFAULT_SETTINGS only if settings[setting] is null or undefined
  return settings[setting] ?? DEFAULT_SETTINGS[setting];
}


/**
 * Deletes a value from localStorage
 */
export const deleteStoredValue = (key: StoreValue): void => {
  window.localStorage.removeItem(key);
}

/**
 * Set a value to be stored in localStorage
 */
const _setStoredValue = (key: string, value: object): void => {
  // use svelte localStorage based persisted store
  // persisted(key, {}).set(value); // uses JSON.stringify under the hood
  window.localStorage.setItem(key, JSON.stringify(value));
}

/**
 * Get a value stored in localStorage
 */
const _getStoredValue = (key: string): object | null => {
  const rawValue = window.localStorage.getItem(key);
  if (rawValue === null || rawValue === undefined) { // does not exists
    return null;
  }
  try {
    return JSON.parse(rawValue);
  } catch (err) {
    logError(`settings: Invalid JSON in storage for ${key} = "${rawValue}"`, err);
  }
  return null; // invalid JSON value
}

/**
 * Stores a value in local storage
 */
export const setStoredValue = (key: StoreValue, value: any): void => { // eslint-disable-line  @typescript-eslint/no-explicit-any
  _setStoredValue(key, value);
}

/**
 * Get a value from local storage
 */
export const getStoredValue = (key: StoreValue, defaultValue: any = null): any => { // eslint-disable-line  @typescript-eslint/no-explicit-any
  const value = _getStoredValue(key);
  return value ?? defaultValue;
}

/**
 * Stores a value in local storage
 */
export const setRawStoredValue = (key: string, value: object): void => {
  _setStoredValue(PREFIX + key, value);
}

/**
 * Get a value from local storage
 */
export const getRawStoredValue = (key: string, defaultValue: any = null): any => { // eslint-disable-line  @typescript-eslint/no-explicit-any
  const value = _getStoredValue(PREFIX + key);
  return value ?? defaultValue;
}

/**
 * 
 * @returns A JSON dump of the current local store
 */
export const localStorageExport = () => {
  return JSON.stringify(localStorage, null, 2);
}

/**
 * A JSON import to the current local store
 * @param data the data to import ot hte local store
 */
export const localStorageImport = (data: any) => { // eslint-disable-line  @typescript-eslint/no-explicit-any
  Object.keys(data).forEach(function (key) { localStorage.setItem(key, data[key]) })
}


/** Matching the old form #live-settings storage  */
export const SETTING_KEY_V0 = 'f7form-live-settings';

/**
 * Detect previous settings and update them if needed
 */
export const setupSettings = (): boolean => {
  if (_getStoredValue(StoreValue.settings) !== null) {
    return false; // we have settings not a first start
  }
  console.log(`Welcome to ${APP_NAME} ! (first start)`) // TODO move in app init
  // first start, setup default settings
  setSettings(DEFAULT_SETTINGS);
  return true; // first start
}

// settings initialization, migrate old settings if needed
const firstStart = setupSettings();

/**
 * @returns true if it's the very first start of the application
 */
export const isFirstStart = (): boolean => {
  return firstStart;
}

export enum SessionType {
  coherence = 1, // cardiac coherence
  meditation = 2, // timer
};

/**
 * Represents the date, length of a Coherence session
 */
export interface SessionStats {
  /** date as number @see Date.getTime() */
  date: number,
  /** how long in seconds */
  elapsed: number,
  /** type of session */
  type: SessionType,
}

const createCleanStat = (stat: SessionStats): SessionStats => {
  return {
    date: stat.date,
    elapsed: Math.abs(Math.round(stat.elapsed)),
    type: stat.type
  };
}

/**
 * Adds a session to the stored statistics
 */
export const addSessionStats = (stat: SessionStats) => {
  const sessionStats = getSessionStats();
  const cleanStat = createCleanStat(stat); // data cleanup
  sessionStats.push(cleanStat)
  setStoredValue(StoreValue.session_stats, sessionStats);
  console.debug('settings:addSessionStats', { ...cleanStat, date: new Date(cleanStat.date) }); // better date display
}

/**
 * Remove a session to the stored statistics
 */
export const removeSessionStats = (date: number): SessionStats[] => {
  const sessionStats = getSessionStats();
  // delete from stats
  let found = false;
  for (let i = 0; i < sessionStats.length; i++) {
    if (sessionStats[i].date === date) {
      sessionStats.splice(i, 1); // delete
      setStoredValue(StoreValue.session_stats, sessionStats); // save
      found = true;
      break;
    }
  }
  console.debug(`settings:removeSessionStats for ${new Date(date)} found: ${found}`);
  return sessionStats;
}


/**
 * Get the history of Coherence sessions
 */
export const getSessionStats = (): SessionStats[] => {
  return getStoredValue(StoreValue.session_stats, []);
}

/**
 * Set the history of Coherence sessions
 */
export const setSessionStats = (stats: SessionStats[]) => {
  const cleanStats: SessionStats[] = [];
  for (const stat of stats) {
    cleanStats.push(createCleanStat(stat));
  }
  return setStoredValue(StoreValue.session_stats, cleanStats);
}


/**
 * First time virgin or was there any previous stored sessions
 */
export const isFirstCoherenceSession = () => {
  return getSessionStats().length == 0;
}

/**
 * Get the saved user or an empty named one
 */
export const getUser = (): User => {
  return getStoredValue(StoreValue.userinfo, { name: '' });
}

/**
 * Store user information
 */
export const setUser = (user: User): void => {
  return setStoredValue(StoreValue.userinfo, user);
}

/**
 * Check is the user name is debug
 */
export const isDebugUser = (): boolean => {
  return getUser().name?.toLowerCase() === 'debug';
}

/**
 * Increment the app start count
 */
export const incrementStartCount = (): number => {
  const count = getStoredValue(StoreValue.start_count, 0) + 1;
  setStoredValue(StoreValue.start_count, count);
  console.debug(`settings:incrementStartCount to ${count}`);
  return count;
}

/**
 * Check last version stored in the settings and compare to the current version
 * of the app to know if it was updated. Updates the last version stored in the settings when called.
 */
export const checkAppWasUpdated = (currentVersion: string) => {
  const lastVersion = getStoredValue(StoreValue.last_version, "");
  const wasUpdated = lastVersion !== currentVersion;
  if (wasUpdated) { // update version in settings if there's an update
    setStoredValue(StoreValue.last_version, currentVersion);
  }
  return {
    updated: wasUpdated,
    lastVersion: lastVersion,
    currentVersion: currentVersion
  }
}

/**
 * Get the app start count
 */
export const getStartCount = (): number => {
  return getStoredValue(StoreValue.start_count, 0);
}